import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import {
  ArrowLeft,
  Download,
  ShieldCheck,
  Smartphone,
  CheckCircle2,
  HelpCircle,
  MessageCircle
} from 'lucide-react';

const APK_URL = 'https://storage.iplsistem.id/releases/IPL%20V.3.0.1.apk';

const steps = [
  {
    step: 1,
    title: 'Unduh File APK',
    desc: 'Unduh file APK via tautan atau scan QR code di atas.',
    details: 'Tekan tombol "Unduh File APK Sekarang" atau scan QR Code langsung dari smartphone Android Anda.',
    icon: Download,
    badgeColor: 'bg-brand-100 text-brand-700',
  },
  {
    step: 2,
    title: 'Izinkan Sumber Tidak Dikenal',
    desc: 'Buka file & aktifkan izin "Izinkan dari sumber ini / Allow unknown source" jika diminta.',
    details: 'Pada notifikasi sistem Android atau Settings > Apps > Chrome/Browser, aktifkan opsi "Izinkan instalasi aplikasi dari sumber ini".',
    icon: ShieldCheck,
    badgeColor: 'bg-gold-100 text-gold-700',
  },
  {
    step: 3,
    title: 'Pasang Aplikasi & Selesai',
    desc: 'Klik Install dan tunggu hingga selesai.',
    details: 'Tekan "Install / Pasang", tunggu beberapa detik, lalu buka aplikasi Sistem IPL untuk mulai login dan mengelola iuran.',
    icon: CheckCircle2,
    badgeColor: 'bg-emerald-100 text-emerald-700',
  },
];

export const DownloadApk = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-background to-gold-400/10 text-foreground">
      {/* Top Navbar Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-brand-700 rounded-lg flex items-center justify-center p-1.5 shadow-sm">
              <img src="/logo-ipl-removebg.webp" alt="Logo Sistem IPL" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl sm:text-2xl font-bold text-brand-700">
              Sistem IPL
            </span>
          </div>

          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-foreground/80 hover:text-brand-700 hover:bg-brand-100/50 flex items-center gap-2"
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline">Kembali ke Beranda</span>
            <span className="sm:hidden">Kembali</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 max-w-5xl">
        {/* Header Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-100 text-brand-800 rounded-full text-xs sm:text-sm font-semibold mb-4 border border-brand-200">
            <Smartphone size={16} className="text-brand-700" />
            <span>Aplikasi Android • IPL</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-foreground tracking-tight mb-4">
            Unduh Aplikasi <span className="text-brand-700">Sistem IPL</span>
          </h1>
          <p className="text-foreground/70 text-base sm:text-lg max-w-2xl mx-auto">
            Dapatkan kemudahan mengelola iuran pemeliharaan lingkungan, pantau tagihan, dan lakukan pembayaran langsung dari genggaman Anda.
          </p>
        </motion.div>

        {/* Download Box Card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.1 }}
          className="bg-card rounded-3xl p-6 sm:p-10 border border-border shadow-xl relative overflow-hidden mb-16"
        >
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-brand-600 via-brand-500 to-gold-500"></div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left: Download Action & Badges */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 text-xs font-medium text-foreground/70">
                <span className="bg-brand-50 text-brand-700 border border-brand-200 px-3 py-1 rounded-full">
                  Versi 3.0.1 (Terbaru)
                </span>
                <span className="bg-brand-50 text-brand-700 border border-brand-200 px-3 py-1 rounded-full">
                  Android 7.0+
                </span>
                <span className="bg-brand-50 text-brand-700 border border-brand-200 px-3 py-1 rounded-full">
                  Format .APK
                </span>
              </div>

              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
                  Sistem IPL Mobile
                </h2>
                <p className="text-foreground/70 text-sm sm:text-base leading-relaxed">
                  Unduh langsung file instalasi (.apk) untuk smartphone Android Anda. Bebas biaya dan aman digunakan.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  asChild
                  size="lg"
                  className="bg-brand-700 hover:bg-brand-800 text-white px-8 py-6 text-lg font-semibold shadow-lg shadow-brand-700/20 group"
                >
                  <a href={APK_URL} download>
                    <Download className="mr-2 group-hover:translate-y-0.5 transition-transform" size={22} />
                    Unduh File APK Sekarang
                  </a>
                </Button>
              </div>

              <p className="text-xs text-foreground/50 flex items-center justify-center lg:justify-start gap-1.5">
                <ShieldCheck size={16} className="text-brand-600 flex-shrink-0" />
                <span>File APK resmi & telah diverifikasi bebas malware.</span>
              </p>
            </div>

            {/* Right: QR Code for Mobile Scanning */}
            <div className="lg:col-span-5 flex flex-col items-center justify-center p-6 bg-brand-50/70 border border-brand-100 rounded-2xl">
              <div className="p-3 bg-white rounded-xl shadow-md border border-brand-200 mb-3">
                <QRCodeSVG
                  value={APK_URL}
                  size={160}
                  level="H"
                  includeMargin={false}
                />
              </div>
              <p className="font-semibold text-sm text-foreground text-center mb-1">
                Scan via Smartphone
              </p>
              <p className="text-xs text-foreground/60 text-center max-w-xs">
                Arahkan kamera smartphone Anda ke QR code di atas untuk mengunduh langsung ke ponsel.
              </p>
            </div>
          </div>
        </motion.div>

        {/* 3 Step Installation Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.2 }}
          className="mb-16"
        >
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              Petunjuk Cara Instalasi APK
            </h2>
            <p className="text-foreground/70 text-sm sm:text-base max-w-xl mx-auto">
              Ikuti 3 langkah mudah berikut untuk memasang aplikasi Sistem IPL di perangkat Android Anda.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={item.step}
                  className="bg-card rounded-2xl p-6 sm:p-7 border border-border shadow-md hover:shadow-lg transition-all flex flex-col relative overflow-hidden group"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="w-10 h-10 rounded-xl bg-brand-700 text-white font-bold flex items-center justify-center text-lg shadow-sm">
                      {item.step}
                    </span>
                    <div className={`p-2.5 rounded-xl ${item.badgeColor}`}>
                      <IconComponent size={24} />
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-brand-700 transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-sm font-medium text-brand-800 bg-brand-50 p-3 rounded-lg border border-brand-100 mb-3">
                    {item.desc}
                  </p>

                  <p className="text-xs text-foreground/60 leading-relaxed mt-auto">
                    {item.details}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Security & FAQ Tips Card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.3 }}
          className="bg-brand-50 border border-brand-200 rounded-2xl p-6 sm:p-8 mb-12"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-brand-100 rounded-xl text-brand-700 flex-shrink-0 mt-1">
              <HelpCircle size={26} />
            </div>
            <div className="space-y-3">
              <h3 className="text-lg font-bold text-brand-900">
                Pemberitahuan Keamanan Android (Play Protect)
              </h3>
              <p className="text-sm text-brand-800 leading-relaxed">
                Karena aplikasi diunduh langsung di luar Google Play Store, beberapa perangkat Android mungkin menampilkan peringatan keamanan (Google Play Protect).
              </p>
              <div className="bg-white/80 border border-brand-200 rounded-xl p-4 text-xs sm:text-sm text-brand-900 space-y-1.5">
                <p className="font-semibold text-brand-800">Tips jika muncul peringatan:</p>
                <p>1. Klik <strong>"Detail selengkapnya" / "More details"</strong>.</p>
                <p>2. Pilih opsi <strong>"Tetap instal" / "Install anyway"</strong>.</p>
                <p>3. Aplikasi Sistem IPL aman dan tidak membahayakan perangkat Anda.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Assistance / Support CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.35 }}
          className="bg-brand-900 text-white rounded-3xl p-8 sm:p-10 text-center relative overflow-hidden"
        >
          <div className="max-w-2xl mx-auto space-y-6">
            <h3 className="text-2xl sm:text-3xl font-bold">
              Mengalami Kendala Saat Instalasi?
            </h3>
            <p className="text-white/70 text-sm sm:text-base">
              Tim support kami siap membantu memandu Anda langkah demi langkah dalam proses instalasi aplikasi Sistem IPL.
            </p>
            <a
              href="https://wa.me/6287886815136?text=Halo%20Admin%20Sistem%20IPL,%20saya%20butuh%20bantuan%20terkait%20instalasi%20aplikasi%20APK"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-brand-900 font-bold px-8 py-4 rounded-xl shadow-lg transition-transform hover:scale-105"
            >
              <MessageCircle size={22} />
              Hubungi Support WhatsApp
            </a>
          </div>
        </motion.div>
      </main>

      {/* Simple Footer */}
      <footer className="border-t border-border py-8 text-center text-xs text-foreground/50">
        <div className="container mx-auto px-4">
          © {new Date().getFullYear()} Sistem IPL. All rights reserved. Powered by Nuratech.
        </div>
      </footer>
    </div>
  );
};
